import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mountain, Save, RotateCcw, Loader2, CheckCircle2 } from "lucide-react";

const CODE = "cat_nat_impresa";
const PARTITE = [["fabbricato", "Fabbricato"], ["macchinario", "Macchinario"], ["merci", "Merci"], ["terreni", "Terreni"]];
const EVENTI = [["terremoto", "Terremoto"], ["inondazione", "Inondazione/Alluvione"], ["franamento", "Franamento"]];

export default function CatNatRates() {
  const [data, setData] = useState(null);
  const [rates, setRates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const fetchRates = () => {
    setLoading(true);
    api.get(`/admin/products/${CODE}/catnat-rates`)
      .then(({ data }) => { setData(data); setRates(structuredClone(data.rates)); })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(fetchRates, []);

  const setBase = (ev, part, val) => setRates((r) => ({ ...r, base: { ...r.base, [ev]: { ...r.base[ev], [part]: val === "" ? "" : Number(val) } } }));
  const setNested = (group, key, val) => setRates((r) => ({ ...r, [group]: { ...r[group], [key]: val === "" ? "" : Number(val) } }));
  const setScalar = (key, val) => setRates((r) => ({ ...r, [key]: val === "" ? "" : Number(val) }));

  const save = async () => {
    setBusy("save");
    try {
      await api.put(`/admin/products/${CODE}/catnat-rates`, { rates });
      setData((d) => ({ ...d, is_custom: true }));
      toast.success("Tabella tassi salvata");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const reset = async () => {
    setBusy("reset");
    try {
      const { data } = await api.post(`/admin/products/${CODE}/catnat-rates/reset`);
      setRates(structuredClone(data.rates));
      setData((d) => ({ ...d, is_custom: false, rates: data.rates }));
      toast.success("Ripristinati i tassi predefiniti");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  if (loading || !rates) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-forest" /></div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 font-heading text-3xl font-semibold text-forest">
          <Mountain className="h-7 w-7 text-terracotta" /> Tassi CAT NAT
        </h1>
        <p className="mt-1 text-muted-foreground">Tabella tariffaria del prodotto «CAT NAT Assimoco». Tassi base in per mille (‰) — baseline zona sismica 4 / alluvione media. Le modifiche si applicano ai nuovi calcoli.</p>
        <span className={`mt-2 inline-flex items-center gap-1.5 text-xs ${data.is_custom ? "text-terracotta" : "text-emerald-600"}`}>
          <CheckCircle2 className="h-3.5 w-3.5" /> {data.is_custom ? "Tabella personalizzata attiva" : "Tabella predefinita"}
        </span>
      </div>

      <Card className="rounded-xl border-border/60 p-5">
        <h2 className="mb-3 font-heading text-lg font-semibold text-forest">Tassi base (‰) — evento × partita</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-muted-foreground">
              <th className="py-2">Evento</th>{PARTITE.map(([c, l]) => <th key={c} className="px-2">{l}</th>)}
            </tr></thead>
            <tbody>
              {EVENTI.map(([ev, evl]) => (
                <tr key={ev} className="border-t border-border/50">
                  <td className="py-2 pr-2 font-medium text-forest">{evl}</td>
                  {PARTITE.map(([part]) => (
                    <td key={part} className="px-1 py-1">
                      <Input data-testid={`rate-${ev}-${part}`} type="number" step="0.001" className="h-9 w-24"
                        value={rates.base[ev][part]} onChange={(e) => setBase(ev, part, e.target.value)} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        <Card className="rounded-xl border-border/60 p-5">
          <h2 className="mb-3 font-heading text-lg font-semibold text-forest">Fattore zona sismica (×Terremoto)</h2>
          <div className="grid grid-cols-4 gap-3">
            {["1", "2", "3", "4"].map((z) => (
              <div key={z} className="space-y-1"><Label className="text-xs">Zona {z}</Label>
                <Input data-testid={`zona-${z}`} type="number" step="0.1" className="h-9" value={rates.zona_sismica_factor[z]} onChange={(e) => setNested("zona_sismica_factor", z, e.target.value)} /></div>
            ))}
          </div>
        </Card>
        <Card className="rounded-xl border-border/60 p-5">
          <h2 className="mb-3 font-heading text-lg font-semibold text-forest">Fattore alluvione (×Inondazione)</h2>
          <div className="grid grid-cols-3 gap-3">
            {["bassa", "media", "alta"].map((c) => (
              <div key={c} className="space-y-1"><Label className="text-xs capitalize">{c}</Label>
                <Input data-testid={`alluvione-${c}`} type="number" step="0.1" className="h-9" value={rates.alluvione_factor[c]} onChange={(e) => setNested("alluvione_factor", c, e.target.value)} /></div>
            ))}
          </div>
        </Card>
        <Card className="rounded-xl border-border/60 p-5">
          <h2 className="mb-3 font-heading text-lg font-semibold text-forest">Minimi e imposta</h2>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="text-xs">Min. riga beni (€)</Label>
              <Input data-testid="min-beni" type="number" step="0.01" className="h-9" value={rates.min_riga.beni} onChange={(e) => setNested("min_riga", "beni", e.target.value)} /></div>
            <div className="space-y-1"><Label className="text-xs">Min. riga terreni (€)</Label>
              <Input data-testid="min-terreni" type="number" step="0.01" className="h-9" value={rates.min_riga.terreni} onChange={(e) => setNested("min_riga", "terreni", e.target.value)} /></div>
            <div className="space-y-1"><Label className="text-xs">Premio minimo polizza (€)</Label>
              <Input data-testid="min-polizza" type="number" step="1" className="h-9" value={rates.min_premio_polizza} onChange={(e) => setScalar("min_premio_polizza", e.target.value)} /></div>
            <div className="space-y-1"><Label className="text-xs">Imposta (es. 0.2225)</Label>
              <Input data-testid="tax" type="number" step="0.0001" className="h-9" value={rates.tax} onChange={(e) => setScalar("tax", e.target.value)} /></div>
          </div>
        </Card>
        <Card className="rounded-xl border-border/60 p-5">
          <h2 className="mb-3 font-heading text-lg font-semibold text-forest">Fattore limite indennizzo (×tutte le garanzie)</h2>
          <div className="grid grid-cols-2 gap-3">
            {["100", "70"].map((l) => (
              <div key={l} className="space-y-1"><Label className="text-xs">Limite {l}%</Label>
                <Input data-testid={`limite-${l}`} type="number" step="0.001" className="h-9" value={(rates.limite_indennizzo_factor || {})[l] ?? ""} onChange={(e) => setNested("limite_indennizzo_factor", l, e.target.value)} /></div>
            ))}
          </div>
        </Card>
        <Card className="rounded-xl border-border/60 p-5">
          <h2 className="mb-3 font-heading text-lg font-semibold text-forest">Fattore materiale (×Terremoto)</h2>
          <div className="grid grid-cols-2 gap-3">
            {Object.keys(rates.materiale_factor || {}).map((m) => (
              <div key={m} className="space-y-1"><Label className="text-xs">{m}</Label>
                <Input type="number" step="0.05" className="h-9" value={rates.materiale_factor[m]} onChange={(e) => setNested("materiale_factor", m, e.target.value)} /></div>
            ))}
          </div>
        </Card>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button onClick={save} disabled={busy === "save"} data-testid="catnat-rates-save" className="gap-1.5 bg-forest hover:bg-forest-hover">
          {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva tassi
        </Button>
        <Button onClick={reset} disabled={busy === "reset" || !data.is_custom} variant="outline" data-testid="catnat-rates-reset" className="gap-1.5">
          {busy === "reset" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Ripristina predefiniti
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground">Nota: i tassi sono provvisori (calibrati sui dati Sardegna/zona 4). Affinare con preventivi reali a premio pieno di zone sismiche diverse.</p>
    </div>
  );
}
